/**
 * ScheduleDemoPage — the standalone "/schedule-demo" page: a custom URL whose ONLY
 * job is scheduling a demo. Shareable on its own (linked from the footer Index and
 * the separately-deployed stories blog), it reuses the landing's brand chrome so it
 * feels like part of the site rather than a bare form.
 *
 * Rebuilt with ONLY real RetroUI + Tailwind (ZERO @mui/*). Like LandingPage it renders
 * standalone (no MUI theme wrapper), so it self-imports the self-hosted fonts and the
 * landing-scoped RetroUI stylesheet (`retroui.css`) — the latter pulls in Tailwind's
 * layers + RetroUI :root tokens so every utility class resolves on this route even when
 * a visitor lands here directly without first visiting "/".
 *
 * Layout: sticky LandingNav → a two-column band (left: brand hero + "what to expect";
 * right: the bordered form panel) → LandingFooter. The form is the shared
 * DemoRequestForm (the SAME implementation that backs the popup dialog), tagged
 * source 'demo'.
 *
 * Mounts inside a `.retroui-root` ancestor, so brand tokens + the scoped Preflight-off
 * reset apply. Preflight is OFF, so every border is written with `border-solid`.
 */

// Self-hosted fonts: Archivo Black headings, Space Grotesk body, Space Mono mono.
import '@fontsource/archivo-black/400.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/space-mono/400.css';
import '@fontsource/space-mono/700.css';

// Landing-scoped RetroUI stylesheet: Tailwind layers (Preflight OFF) + RetroUI tokens.
import './retroui.css';

import * as React from 'react';
import { CalendarClock, MessagesSquare, MonitorPlay } from 'lucide-react';
import { LandingNav, LandingFooter, DemoRequestForm } from './sections';

/** "What to expect" bullets, flanking the form so the page reads as more than a form.
 *  Each badge is a brand-colored square, rotating through the accent palette. The icon
 *  color flips per background for contrast: BLACK on the light yellow, WHITE on the
 *  darker green + teal. */
const EXPECTATIONS: ReadonlyArray<{
  icon: React.ReactNode;
  title: string;
  body: string;
  /** Brand background utility for the icon badge. */
  bgClass: string;
  /** Icon color utility, chosen for contrast against `bgClass`. */
  iconClass: string;
}> = [
  {
    icon: <MessagesSquare className="h-6 w-6" aria-hidden="true" />,
    title: 'A real conversation',
    body: 'Tell us what you’re measuring and who your participants are — no sales script.',
    bgClass: 'bg-yellow',
    iconClass: 'text-foreground',
  },
  {
    icon: <MonitorPlay className="h-6 w-6" aria-hidden="true" />,
    title: 'A live walkthrough',
    body: 'See the researcher dashboard, device onboarding, and per-participant export in action.',
    bgClass: 'bg-green',
    iconClass: 'text-white',
  },
  {
    icon: <CalendarClock className="h-6 w-6" aria-hidden="true" />,
    title: 'A fast reply',
    body: 'We read every request and follow up from support@studysyncs.com to find a time.',
    bgClass: 'bg-secondary',
    iconClass: 'text-white',
  },
];

const ScheduleDemoPage: React.FC = () => {
  // Match RetroLanding: disable page-level overscroll bounce while mounted; restore on
  // unmount so other routes are unaffected.
  React.useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.overscrollBehavior;
    html.style.overscrollBehavior = 'none';
    return () => {
      html.style.overscrollBehavior = prev;
    };
  }, []);

  return (
    <div className="retroui-root min-h-screen overflow-x-clip bg-background font-sans text-foreground antialiased">
      <LandingNav />

      <main className="mx-auto max-w-[1240px] px-4 py-14 md:px-8 md:py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* LEFT — brand hero + "what to expect". */}
          <div className="lg:pt-4">
            {/* Eyebrow badge. */}
            <span className="inline-block border-2 border-solid border-black bg-secondary px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">
              Schedule a demo
            </span>

            <h1 className="mt-6 font-head text-4xl uppercase leading-[0.95] tracking-tight text-foreground sm:text-5xl md:text-6xl">
              See StudySync
              <br />
              <span className="text-primary">in action</span>
            </h1>

            <p className="mt-6 max-w-md font-sans text-lg text-muted-foreground">
              Walk through the platform with our team — wearable + EHR collection,
              the live researcher dashboard, and per-participant export, tailored to
              your study.
            </p>

            {/* What-to-expect list. */}
            <ul className="mt-10 flex flex-col gap-5">
              {EXPECTATIONS.map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center border-2 border-solid border-black ${item.bgClass} ${item.iconClass}`}
                  >
                    {item.icon}
                  </span>
                  <div>
                    <h2 className="font-sans text-base font-bold uppercase tracking-wide text-foreground">
                      {item.title}
                    </h2>
                    <p className="mt-1 font-sans text-sm text-muted-foreground">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT — the bordered form panel (shared DemoRequestForm). */}
          <div className="border-2 border-solid border-black bg-card p-7 shadow-brand md:p-10">
            <DemoRequestForm source="demo" showHeading={false} />
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default ScheduleDemoPage;
